import React, { useEffect, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import { useQueryClient } from "@tanstack/react-query";
import { SiteSystemType } from "api/ApiClient";
import {
  apiClientFactory,
  defaultSiteTimeZone,
  getComprehensiveTimeZones,
  isKnownIanaTimeZone,
  isNorthAmericaTimeZoneForDisplay,
  preferredSiteTimeZones,
  type TimeZoneOption,
} from "../../helpers/index";
import { TimeZoneCombobox } from "components/shared/TimeZoneCombobox";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Switch } from "components/ui/switch";
import FobTypeChangeDialog from "./FobTypeChangeDialog";
import {
  DEFAULT_FOB_TYPE,
  DEFAULT_FOB_TYPE_OPTIONS,
  type DefaultFobType,
} from "./fobTypes";
import {
  CADENCE_DEFAULT,
  CLEAR_INTERVAL_MINUTES,
  DB_DOWNLOAD_INTERVAL_OPTIONS,
  DEFAULT_CADENCE_LABEL,
  hasDaytimeCallIns,
  minuteOfDayToTime,
  timeToMinuteOfDay,
} from "./dbDownloadCadence";
import PhysicalKeySyncSuggestionDialog from "components/shared/PhysicalKeySyncSuggestionDialog";
import {
  type PhysicalKeySyncSuggestion,
  physicalKeySyncSuggestionsOf,
} from "components/shared/physicalKeySyncSuggestions";

interface SiteEditValues {
  name: string;
  propertyExternalId: string;
  timeZone: string;
  systemSiteId?: string;
  accessCode?: string;
  dormaRemoveKeyEnabled?: boolean;
  schlageSharePhysicalKeysEnabled?: boolean;
  defaultFobType?: DefaultFobType;
  dbDownloadIntervalMinutes?: number | null;
  dbDownloadAnchorMinuteOfDay?: number | null;
}

interface SiteEditModalProps {
  open: boolean;
  siteId: string;
  initialValues: SiteEditValues;
  systemType?: string;
  onClose: (saved?: boolean) => void;
}

export default function SiteEditModal({
  open,
  siteId,
  initialValues,
  systemType,
  onClose,
}: SiteEditModalProps) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<SiteEditValues>(initialValues);
  const [timeZoneSearchText, setTimeZoneSearchText] = useState(initialValues.timeZone ?? "");
  const [customTimeZone, setCustomTimeZone] = useState("");
  const [confirmFobTypeOpen, setConfirmFobTypeOpen] = useState(false);
  // Fobs syncs the save suggests; shown after the edit dialog closes.
  const [syncSuggestions, setSyncSuggestions] = useState<PhysicalKeySyncSuggestion[]>([]);

  const isSchlage = systemType === (SiteSystemType.SchlageEngage as string);
  const isDorma = systemType === (SiteSystemType.DormaKaba as string);
  const isOpenPathOrBrivo = systemType === (SiteSystemType.Openpath as string) ||
    systemType === (SiteSystemType.Brivo as string);

  useEffect(() => {
    if (open) {
      setForm(initialValues);
      setTimeZoneSearchText(initialValues.timeZone ?? "");
      setCustomTimeZone("");
      setConfirmFobTypeOpen(false);
    }
  }, [open, initialValues]);

  // An absent value means the site predates the setting, i.e. the default.
  const initialFobType = initialValues.defaultFobType ?? DEFAULT_FOB_TYPE;
  const selectedFobType = form.defaultFobType ?? initialFobType;
  const fobTypeChanged = isSchlage && selectedFobType !== initialFobType;

  // Null interval: no cadence, the locks use their own default. Null anchor: midnight.
  const initialInterval = initialValues.dbDownloadIntervalMinutes ?? null;
  const initialAnchor = initialValues.dbDownloadAnchorMinuteOfDay ?? null;
  const selectedInterval = form.dbDownloadIntervalMinutes ?? null;
  const selectedAnchor = form.dbDownloadAnchorMinuteOfDay ?? null;
  const cadenceChanged = isSchlage &&
    (selectedInterval !== initialInterval || (selectedInterval !== null && selectedAnchor !== initialAnchor));

  const timeZoneOptions = useMemo<TimeZoneOption[]>(() => {
    const options = getComprehensiveTimeZones([])
      .filter(isNorthAmericaTimeZoneForDisplay)
      .map((tz) => ({
        value: tz,
        isPreferred: preferredSiteTimeZones.includes(tz),
        isInUse: false,
      }));
    const selected = (form.timeZone ?? "").trim();
    if (selected && !options.some((o) => o.value === selected)) {
      options.unshift({ value: selected, isPreferred: false, isInUse: false });
    }
    return options;
  }, [form.timeZone]);

  const selectedOption = useMemo(
    () => timeZoneOptions.find((o) => o.value === form.timeZone) ?? null,
    [form.timeZone, timeZoneOptions],
  );

  const effectiveTimeZone = (customTimeZone || form.timeZone || "").trim();
  const resolvedTimeZone = effectiveTimeZone || defaultSiteTimeZone;
  const hasUnknownCustomTimeZone =
    !!customTimeZone.trim() && !isKnownIanaTimeZone(customTimeZone.trim());

  const filterOptions = (options: TimeZoneOption[], inputValue: string) => {
    const q = inputValue.trim().toLowerCase();
    if (!q) return options.slice(0, 200);
    const filtered = options.filter((o) => o.value.toLowerCase().includes(q));
    if (!options.some((o) => o.value.toLowerCase() === q)) {
      filtered.push({ value: inputValue.trim(), isPreferred: false, isInUse: false, isCustomAction: true });
    }
    return filtered.slice(0, 200);
  };

  const set = (patch: Partial<SiteEditValues>) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setLoading(true);
    try {
      const payload: Record<string, unknown> = {
        name: form.name,
        propertyExternalId: form.propertyExternalId,
        timeZone: resolvedTimeZone,
      };
      if (isSchlage) {
        if (form.accessCode !== undefined) payload.accessCode = form.accessCode;
        if (form.schlageSharePhysicalKeysEnabled !== undefined)
          payload.schlageSharePhysicalKeysEnabled = form.schlageSharePhysicalKeysEnabled;
        if (form.defaultFobType !== undefined) payload.defaultFobType = form.defaultFobType;
        // The API refuses a fob type change without this; it's only sent once the warning has been confirmed.
        if (fobTypeChanged) payload.confirmDefaultFobTypeChange = true;
        if (cadenceChanged) {
          if (selectedInterval === null) {
            // Clears the interval and its anchor.
            payload.dbDownloadIntervalMinutes = CLEAR_INTERVAL_MINUTES;
          } else {
            payload.dbDownloadIntervalMinutes = selectedInterval;
            payload.dbDownloadAnchorMinuteOfDay = selectedAnchor ?? 0;
          }
        }
      }
      if (isDorma && form.dormaRemoveKeyEnabled !== undefined) {
        payload.dormaRemoveKeyEnabled = form.dormaRemoveKeyEnabled;
      }
      const saved = await apiClientFactory().updateSite(siteId, payload as any);
      setSyncSuggestions(physicalKeySyncSuggestionsOf(saved));
      queryClient.invalidateQueries({ queryKey: ["sites", "details", siteId] });
      queryClient.invalidateQueries({ queryKey: ["sites-admin"] });
      if (fobTypeChanged) {
        // The fob number shown for this site's keys may have changed.
        queryClient.invalidateQueries({ queryKey: ["sites", "keys"] });
        queryClient.invalidateQueries({ queryKey: ["keys", siteId] });
      }
      enqueueSnackbar(<span style={{ fontSize: 12 }}>Site updated successfully</span>, { variant: "success" });
      setConfirmFobTypeOpen(false);
      onClose(true);
    } catch (err: any) {
      enqueueSnackbar(
        <span style={{ fontSize: 12 }}>{err?.message ?? "Error updating site"}</span>,
        { variant: "error" },
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (fobTypeChanged) {
      // Changing the fob type reinterprets every key at the site: confirm first, then save.
      setConfirmFobTypeOpen(true);
      return;
    }
    await save();
  };

  return (
    <>
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        showCloseButton={false}
        className="sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle>Edit Site</DialogTitle>
        </DialogHeader>
        <form className="grid gap-4" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="se-name">Site Name</label>
            <input type="text" id="se-name" className="form-control" required
              value={form.name} onChange={(e) => set({ name: e.target.value })} />
          </div>
          <div className="form-group">
            <label htmlFor="se-propertyExternalId">Property External ID</label>
            <input type="text" id="se-propertyExternalId" className="form-control"
              value={form.propertyExternalId} onChange={(e) => set({ propertyExternalId: e.target.value })} />
          </div>
          <div className="form-group">
            <label htmlFor="se-timeZone">Time Zone</label>
            <TimeZoneCombobox
              ariaLabel="Time Zone"
              filterOptions={filterOptions}
              id="se-timeZone"
              inputValue={timeZoneSearchText}
              onClear={() => {
                setCustomTimeZone("");
                set({ timeZone: "" });
                setTimeZoneSearchText("");
              }}
              onInputValueChange={setTimeZoneSearchText}
              onSelectOption={(option) => {
                if (option.isCustomAction) {
                  setCustomTimeZone(option.value.trim());
                  set({ timeZone: "" });
                  setTimeZoneSearchText(option.value.trim());
                  return;
                }

                setCustomTimeZone("");
                set({ timeZone: option.value });
                setTimeZoneSearchText(option.value);
              }}
              options={timeZoneOptions}
              placeholder="Search timezone"
              value={selectedOption}
            />
            {hasUnknownCustomTimeZone && (
              <small className="form-text text-warning">Not a known IANA timezone — you can still save.</small>
            )}
          </div>

          {/* Schlage-specific */}
          {isSchlage && (
            <>
              <div className="form-group">
                <label htmlFor="se-accessCode">Access Code</label>
                <input id="se-accessCode" className="form-control" placeholder="Access code" maxLength={12}
                  value={form.accessCode ?? ""} onChange={(e) => set({ accessCode: e.target.value })} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <div className="flex items-center gap-2">
                  <Switch
                    id="se-schlage-share-physical-keys"
                    checked={form.schlageSharePhysicalKeysEnabled ?? false}
                    onCheckedChange={(checked) =>
                      set({ schlageSharePhysicalKeysEnabled: checked })
                    }
                  />
                  <label htmlFor="se-schlage-share-physical-keys">
                    Share Physical Keys
                  </label>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="se-default-fob-type">Default Fob Type</label>
                <Select
                  value={selectedFobType}
                  onValueChange={(value) => set({ defaultFobType: value as DefaultFobType })}
                >
                  <SelectTrigger aria-label="Default Fob Type" className="w-full" id="se-default-fob-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DEFAULT_FOB_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <small className="form-text text-muted">
                  Controls how physical keys are converted when shared to Brivo sites and which number is shown for
                  each fob. Changing this affects every key at this site.
                </small>
              </div>
              <div className="form-group">
                <label htmlFor="se-db-download-cadence">DB Download Cadence</label>
                <Select
                  value={selectedInterval === null ? CADENCE_DEFAULT : String(selectedInterval)}
                  onValueChange={(value) =>
                    set({ dbDownloadIntervalMinutes: value === CADENCE_DEFAULT ? null : Number(value) })
                  }
                >
                  <SelectTrigger aria-label="DB Download Cadence" className="w-full" id="se-db-download-cadence">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={CADENCE_DEFAULT}>{DEFAULT_CADENCE_LABEL}</SelectItem>
                    {DB_DOWNLOAD_INTERVAL_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={String(option.value)}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedInterval !== null && (
                  <div className="mt-2">
                    <label htmlFor="se-db-download-anchor">
                      Starting at ({resolvedTimeZone})
                    </label>
                    <input
                      className="form-control"
                      id="se-db-download-anchor"
                      required
                      type="time"
                      value={minuteOfDayToTime(selectedAnchor)}
                      onChange={(e) => {
                        const minuteOfDay = timeToMinuteOfDay(e.target.value);
                        if (minuteOfDay !== undefined) set({ dbDownloadAnchorMinuteOfDay: minuteOfDay });
                      }}
                    />
                  </div>
                )}
                <small className="form-text text-muted">
                  Applies to Wi-Fi locks only; gateway-connected locks get updates straight away. Each lock calls in
                  within a few minutes after the start time. A lock briefly ignores cards while it calls in, and each
                  call-in uses battery. Changes take effect after each lock&apos;s next call-in.
                </small>
                {hasDaytimeCallIns(selectedInterval, selectedAnchor) && (
                  <small className="form-text text-warning" role="status">
                    Locks will call in during the day, when residents may notice. Consider a daily overnight time.
                  </small>
                )}
              </div>
            </>
          )}

          {/* DormaKaba-specific */}
          {isDorma && (
            <div style={{ marginBottom: 12 }}>
              <div className="flex items-center gap-2">
                <Switch
                  id="se-dorma-remove-key"
                  checked={form.dormaRemoveKeyEnabled ?? false}
                  onCheckedChange={(checked) =>
                    set({ dormaRemoveKeyEnabled: checked })
                  }
                />
                <label htmlFor="se-dorma-remove-key">
                  Remove Key on Deactivation
                </label>
              </div>
            </div>
          )}

          {/* OpenPath / Brivo — systemSiteId is read-only (not in UpdateSiteRequest) */}
          {isOpenPathOrBrivo && (
            <div className="form-group">
              <label htmlFor="se-systemSiteId">System Site ID</label>
              <input
                id="se-systemSiteId"
                className="form-control"
                value={initialValues.systemSiteId ?? ""}
                disabled
                style={{ backgroundColor: "#f5f5f5", color: "#888", cursor: "not-allowed" }}
              />
              <small className="form-text text-muted">Read-only — contact support to change.</small>
            </div>
          )}
          <DialogFooter>
            <Button disabled={loading} onClick={() => onClose()} type="button" variant="outline">
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
        {/* Portals to the body; nested here so it opens on top of the edit dialog. */}
        <FobTypeChangeDialog
          fromFobType={initialFobType}
          onCancel={() => setConfirmFobTypeOpen(false)}
          onConfirm={() => void save()}
          open={confirmFobTypeOpen}
          saving={loading}
          siteName={form.name || initialValues.name}
          toFobType={selectedFobType}
        />
      </DialogContent>
    </Dialog>
    <PhysicalKeySyncSuggestionDialog
      suggestions={syncSuggestions}
      onClose={() => setSyncSuggestions([])}
    />
    </>
  );
}
